import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addInstructionDTO,
  putInstructionDTO,
  deleteInstructionDTO,
} from "../../dtos/instruction";
import instructionService from "./instructionService";

const initialState = {
  // New state properties related to instruction  
  instructionPayload: [] as any,
  addInstructionPayload: null,
  updateInstructionPayload: null,
  deletePayload: null,

  // Error flags for instruction  
  isInstructionError: false,
  isAddInstructionError: false,
  isUpdateInstructionError: false,
  isDeleteInstructionError: false,

  // Success flags for instruction  
  isInstructionSuccess: false,
  isAddInstructionSuccess: false,
  isUpdateInstructionSuccess: false,
  isDeleteInstructionSuccess: false,

  // Loading flags for instruction  
  isInstructionLoading: false,
  isAddInstructionLoading: false,
  isUpdateInstructionLoading: false,
  isDeleteInstructionLoading: false,


  // Messages for instruction  
  instructionMessage: "",
  addInstructionMessage: "",
  updateInstructionMessage: "",
  deleteInstructionMessage: "",
};

// Async Thunks for instruction   operations

// 1. add instruction 
export const addInstruction = createAsyncThunk(
  "instruction/ /add",
  async (instruction: addInstructionDTO, thunkAPI) => {
    try {
      return await instructionService.addInstruction(instruction); // Call to the service for adding instruction  
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 2. Get instruction
export const getInstruction = createAsyncThunk(
  "instruction/ /get",
  async (id: number, thunkAPI) => {
    try {

      return await instructionService.getInstruction(id); // Correct API call with `params`
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);



// update instruction
export const updateInstruction = createAsyncThunk(
  "instruction/ /update",
  async (instruction: putInstructionDTO, thunkAPI) => {
    try {
      return await instructionService.putInstruction(instruction); // Call to the service for updating instruction  
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// delete instruction
export const deleteInstruction = createAsyncThunk(
  "instruction/ /delete",
  async (instruction: deleteInstructionDTO, thunkAPI) => {
    try {
      return await instructionService.deleteInstruction(instruction); // Correct API call with `data` for DELETE request
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const instructionSlice = createSlice({
  name: "instruction",
  initialState,
  reducers: {
    reset: () => initialState,
    resetInstructionFlags: (state) => {
      state.isAddInstructionSuccess = false;
      state.isUpdateInstructionSuccess = false;
      state.isDeleteInstructionSuccess = false;
      state.isUpdateInstructionError = false;
      state.isDeleteInstructionError = false;
      state.isAddInstructionError = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add instruction  
      .addCase(addInstruction.pending, (state) => {
        state.isAddInstructionLoading = true;
        state.isAddInstructionSuccess = false;
        state.isUpdateInstructionSuccess = false;
        state.isDeleteInstructionSuccess = false;
      })
      .addCase(addInstruction.fulfilled, (state, action) => {
        state.isAddInstructionLoading = false;
        state.isAddInstructionSuccess = true;
        state.addInstructionMessage = "";
        state.addInstructionPayload = action.payload;
      })
      .addCase(addInstruction.rejected, (state, action: PayloadAction<any>) => {
        state.isAddInstructionLoading = false;
        state.isAddInstructionError = true;
        state.isAddInstructionSuccess = false;
        state.addInstructionMessage = action.payload;
      })

      // Get instruction  
      .addCase(getInstruction.pending, (state) => {
        state.isInstructionLoading = true;
      })
      .addCase(getInstruction.fulfilled, (state, action) => {
        state.isInstructionLoading = false;
        state.isInstructionSuccess = true;
        state.instructionMessage = "";
        state.instructionPayload = action?.payload;
      })
      .addCase(getInstruction.rejected, (state, action: PayloadAction<any>) => {
        state.isInstructionLoading = false;
        state.isInstructionError = true;
        state.instructionMessage = action.payload;
      })


      // Update instruction  
      .addCase(updateInstruction.pending, (state) => {
        state.isUpdateInstructionLoading = true;
        state.isUpdateInstructionSuccess = false;
        state.isDeleteInstructionSuccess = false;
        state.isAddInstructionSuccess = false;
      })
      .addCase(updateInstruction.fulfilled, (state, action) => {
        state.isUpdateInstructionLoading = false;
        state.isUpdateInstructionSuccess = true;
        state.updateInstructionMessage = "";
        state.updateInstructionPayload = action.payload;
      })
      .addCase(updateInstruction.rejected, (state, action: PayloadAction<any>) => {
        state.isUpdateInstructionLoading = false;
        state.isUpdateInstructionError = true;
        state.isUpdateInstructionSuccess = false;
        state.updateInstructionMessage = action.payload;
      })

      // Delete instruction  
      .addCase(deleteInstruction.pending, (state) => {
        state.isDeleteInstructionLoading = true;
        state.isUpdateInstructionSuccess = false;
        state.isDeleteInstructionSuccess = false;
        state.isAddInstructionSuccess = false;
      })
      .addCase(deleteInstruction.fulfilled, (state, action) => {
        state.isDeleteInstructionLoading = false;
        state.isDeleteInstructionSuccess = true;
        state.deleteInstructionMessage = "";
        state.deletePayload = action.payload;
      })
      .addCase(deleteInstruction.rejected, (state, action: PayloadAction<any>) => {
        state.isDeleteInstructionLoading = false;
        state.isDeleteInstructionError = true;
        state.isDeleteInstructionSuccess = false;
        state.deleteInstructionMessage = action.payload;
      })
     
  },
});

export const { reset } = instructionSlice.actions;
export const { resetInstructionFlags } = instructionSlice.actions;
export default instructionSlice.reducer;
